import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog'
import { faEdit, faPlus, faUsers, faWarning, faFire, faImagePortrait, faImage } from '@fortawesome/free-solid-svg-icons';
import { MatTableDataSource } from '@angular/material/table';
import { AssessmentService } from 'src/app/services/assessment.service';
import { Observable, map, of, startWith } from 'rxjs';
import { FormControl } from '@angular/forms';
import { DialogService } from 'src/app/services/dialog.service';
import { Title } from '@angular/platform-browser';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { UserService } from 'src/app/services/user.service';
import { ComplexityModalComponent } from '../../fire-tracker/complexity-modal/complexity-modal.component';
import { EditAssessmentComponent } from '../edit-assessment/edit-assessment.component';
import { RequestSupportComponent } from '../request-support/request-support.component';
import { ReportService } from 'src/app/services/report.service';
import { ConfirmationToastComponent } from 'src/app/components/confirmation-toast/confirmation-toast.component';
import { BarcRequestComponent } from "../barc-request/barc-request.component";

@Component({
  selector: 'app-baer-assessments-tab',
  templateUrl: './baer-assessments-tab.component.html',
  styleUrls: ['./baer-assessments-tab.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed,void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})


export class BaerAssessmentsTabComponent implements OnInit {
  //icons
  faEdit = faEdit;
  faUsers = faUsers;
  faPlus = faPlus;
  faWarning = faWarning;
  faFire = faFire;
  faImage = faImage;

  //table variables
  pageNumber = 0;
  pageSize = 10;
  length = 0;
  sort = "updated_on"
  sortDirection = "desc"
  selectedForestFlag = false;
  assessments = new MatTableDataSource<any>();
  expandedAssessments: any;
  displayedColumns!: string[];

  //dropdown lists
  regions: any;
  statuses: any;
  complexities: any;
  team_leads: any;
  yearsArray: any;

  //access
  @Input() user: any;

  //forest name filter
  forestNameList: string[];
  filtered_forest_names: Observable<string[]>;
  @ViewChild('forest') input?: ElementRef<HTMLInputElement>;
  forest_name = new FormControl("")
  selectedRegion: string | undefined = undefined;

  //search bar items
  filters = {
    region: null,
    forest_name: null as (string | null),
    baer_name: null,
    assessment_status_id: null,
    assessment_status_ids: [1, 3, 4, 11, 12, 14],
    fire_complexity_id: null,
    team_lead_id: null,
    fire_discovery_years: null,
  }
  filters_clean = true;

  constructor(
    public dialog: MatDialog,
    private readonly assessmentService: AssessmentService,
    private readonly dialogService: DialogService,
    private readonly titleService: Title,
    private readonly userService: UserService,
    private readonly reportsService: ReportService,
    public readonly toast: ConfirmationToastComponent,
  ) {
    this.titleService.setTitle("BAER - Assessments")
    this.regions = this.assessmentService.getRegionList();
    this.statuses = this.assessmentService.getStatusList().filter((x: any) => [1, 3, 4, 11, 12, 14].includes(x.value));;
    this.complexities = this.assessmentService.getComplexityList();
    this.userService.getPossibleLeadList().subscribe((team_leads: any) => { this.team_leads = team_leads.users });
    this.forestNameList = this.assessmentService.getForestNameList();
    this.filtered_forest_names = of(this.forestNameList)

    this.assessmentService.finishLoadingSubscription().subscribe(() => {
      this.regions = this.assessmentService.getRegionList();
      this.statuses = this.assessmentService.getStatusList().filter((x: any) => [1, 3, 4, 11, 12, 14].includes(x.value));
      this.complexities = this.assessmentService.getComplexityList();
      this.forestNameList = this.assessmentService.getForestNameList(this.filters.region as any);

      this.filtered_forest_names = of(this.forestNameList)
      this.filtered_forest_names = this.forest_name.valueChanges.pipe(
        startWith(""),
        map((value) => this._filter((value as string) || ""))
      )
    })

    const currentYear = new Date().getFullYear();
    const numberOfYears = 3; // You can change this to the desired number of years
    this.yearsArray = Array.from({ length: numberOfYears }, (_, i) => currentYear - i);
    this.filters.fire_discovery_years = this.yearsArray[0];
    this.filters_clean = false;
  }

  regionChanged(): void {
    this.forestNameList = this.assessmentService.getForestNameList(this.filters.region as any);
    this.filtered_forest_names = this.forest_name.valueChanges.pipe(
      startWith(""),
      map((value) => this._filter((value as string) || ""))
    )
  }

  ngOnInit(): void {
    this.loadUser();

    this.displayedColumns = ['collapse',
      'baer_name',
      'region',
      'assessment_status_id',
      'fire_complexity_id',
      'team_needs',
      'team_lead',
      'actions'
    ]
    this.filtered_forest_names = this.forest_name.valueChanges.pipe(
      startWith(""),
      map((value) => this._filter((value as string) || ""))
    )
  }

  loadUser() {
    if (this.user.isWO) {
      this.loadAssessments();
      return;
    } else if ((this.user.isROCO?.length ?? 0) > 0) {
      this.filters.region = this.user.region;
      this.filters_clean = false;
      this.search();
      return;
    } else if ((this.user.isFOCO?.length ?? 0) > 0) {
      this.filters.region = this.user.region;
      this.forest_name.setValue(this.user.forest_name);
      this.filters_clean = false;
      this.search();
      return;
    } else if (this.user.teamLeadRoles) {
      this.user.isTeamLead = true;
      this.filters_clean = false;
      this.loadAssessments(true);
      return;
    } else {
      //default filter by forest
      this.filters.region = this.user.region;
      this.forest_name.setValue(this.user.forest_name);
      this.filters_clean = false;
    }
    this.search();

  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase()
    return this.forestNameList.filter((option: string) =>
      option.toLowerCase().includes(filterValue)
    )
  }
  filter(): void {
    const filterValue = this.input?.nativeElement.value.toLowerCase() ?? '';
    this.filtered_forest_names = of(this._filter(filterValue));
  }

  search(): void {
    this.filters_clean = false;
    this.pageNumber = 0;
    this.loadAssessments();
  }

  clearSearch(): void {
    this.filters_clean = true;
    this.pageNumber = 0;
    this.filters = {
      region: null,
      forest_name: null,
      baer_name: null,
      assessment_status_id: null,
      assessment_status_ids: [1, 3, 4, 11, 12, 14],
      fire_complexity_id: null,
      team_lead_id: null,
      fire_discovery_years: null,
    }
    this.forest_name.setValue("");
    this.regionChanged();
    this.loadAssessments();
  }

  pageEvent(e: any) {
    this.pageNumber = e.pageIndex;
    this.pageSize = e.pageSize;
    this.loadAssessments();
  }

  addSupportMessage(assessment: any) {
    if (assessment.supports?.length > 0) {
      assessment.support_tooltip = `Interregional Support Request(s)`

      assessment.supports = assessment.supports.sort((a: any, b: any) => {
        if (a.support_type === 'Team Lead') return -1;
        return 0;
      }
      );

      assessment.supports.forEach((support: any) => {
        if (support.support_completed) return
        let skillsText = "";
        let typeText = "";
        if ((support.support_type == 'Team Member(s)' || support.support_type == 'Shortage Team Members') && support.support_skills != "") {
          typeText = support.support_type;
          let skills = support.support_skills.split(", ")
          if (skills.length == 1) {
            skillsText = support.support_skills
          } else if (skills.length == 2) {
            skillsText = skills.join(" and ")
          } else {
            skills[skills.length - 1] = "and " + skills[skills.length - 1]
            skillsText = skills.join(", ")
          }
          skillsText = "\nSkills Needed: " + skillsText
        } else {
          typeText = "Team Lead Request"
        }
        let addDetailsText = ""
        if (support.additional_details) {
          addDetailsText = `\nAdditional Details: ${support.additional_details}`
        } else {
          addDetailsText = `\nAdditional Details: None`
        }
        assessment.support_tooltip += `\n\n${typeText} ${skillsText}${addDetailsText}`
      })
    }
  }

  async loadAssessments(initialTeamLeadLoad?: boolean) {
    this.dialogService.openSpinner()
    this.filters.forest_name = this.forest_name.value ?? null;
    this.assessmentService.getAssessmentList(this.pageNumber + 1, this.pageSize, this.filters, this.sort, this.sortDirection, initialTeamLeadLoad, this.user.FS_ID).subscribe(
      {
        next: (data) => {
          this.assessments.data = data.assessments;
          this.length = data.totalResults;
          if (initialTeamLeadLoad) this.length = this.assessments.data.length;
          this.assessments.data.forEach((assessment: any) => {
            this.addSupportMessage(assessment);
            this.loadReportData(assessment);
          })
        },
        complete: () => {
          this.dialogService.closeSpinner();
          return true;
        },
        error: (err) => {
          return false;
        }
      });
  }

  loadReportData(assessment: any) {
    let reportRelations = '&relations=users.user,assessment,report_status,supports';
    this.reportsService.getReportList(assessment.assessment_id, reportRelations).subscribe(
      {
        next: (data) => {
          let reports = data.reports;
          reports.sort((reportA: any, reportB: any) => reportA.report_num - reportB.report_num);
          reports.forEach((report: any) => {
            report.mainLead = report.users.find((user: any) => user.role_id == 0)?.user;
          });
          assessment.reports = reports;
        }
      });
  }

  sortChange(sortState: any) {
    this.sort = sortState.active;
    this.sortDirection = sortState.direction;
    this.pageNumber = 0;
    this.loadAssessments()
  }

  clearValue(event: any) {
    event.target.value = ""
  }

  clearForest() {
    if (!this.selectedForestFlag) {
      this.forest_name.setValue("")
    }
    this.selectedForestFlag = false
  }

  forestSelected() {
    this.selectedForestFlag = true
  }

  complexityModal(): void {
    this.dialog.open(ComplexityModalComponent, {
      width: "700px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
    })
  }

  editBaerTeamNeeds(assessment: any): void {
    this.dialog.open(EditAssessmentComponent, {
      maxWidth: "1000px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
      data: {
        fireRecord: assessment,
        isTeamLead: this.user.isTeamLead,
        user: this.user
      },
    }).afterClosed().subscribe((reload) => {
      this.titleService.setTitle("BAER - Assessments")
      if (reload) {
        this.loadAssessments();
      }
    })
  }

  requestSupport(assessment: any): void {
    this.dialog.open(RequestSupportComponent, {
      maxWidth: "900px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
      data: {
        fireRecord: assessment,
      },
    }).afterClosed().subscribe((reload) => {
      this.titleService.setTitle("BAER - Assessments")
      if (reload) {
        this.loadAssessments();
      }
    })
  }

  stopPropagation(event: any) {
    event.stopPropagation();
  }

  createInterimReport(assessment: any) {
    this.dialogService.openSpinner();
    let report_num_list = assessment.reports.map((report: any) => { return report.report_num });
    const next_report_num = Math.max(...report_num_list) + 1;
    this.reportsService.createReport(assessment, next_report_num).subscribe({
      next: (response) => {
        this.dialogService.closeSpinner()
        this.toast.openSuccessSnackBar(`An interim report for ${assessment.baer_name} has been created.`);
        this.loadAssessments();
      },
      error: (response) => {
        this.dialogService.closeSpinner()
        this.toast.openErrorSnackBar(`There was an error creating the initial report.`, "Please try again.");
      },
    })
  }

  createInitialReport(assessment: any) {
    this.dialogService.openSpinner()
    this.assessmentService.editAssessment({
      assessment_id: assessment.assessment_id,
      assessment_status_id: 3
    }).subscribe({
      next: (response) => { },
      error: (response) => { },
    })
    this.reportsService.createReport(assessment, 1).subscribe({
      next: (response) => {
        this.dialogService.closeSpinner()
        this.toast.openSuccessSnackBar(`An initial report for ${assessment.baer_name} has been created.`);
        this.loadAssessments();
      },
      error: (response) => {
        this.dialogService.closeSpinner()
        this.toast.openErrorSnackBar(`There was an error creating the initial report.`, "Please try again.");
      },
    })
  }

  editCheck(assessment: any) {
    if (this.user.isWO ||
      (this.user.isTeamLead && (assessment.team_lead?.user_id == this.user.user_id)) ||
      (this.user.isFOCO.reduce((acc: any, item: any) => { if (acc || assessment.forest_name.includes(item)) return true; else return false; }, false)) ||
      (this.user.isROCO.reduce((acc: any, item: any) => { if (acc || assessment.region.includes(item)) return true; else return false; }, false))
    )
      return true;
    else
      return false;
  }

  checkCreateInitialReport(assessment: any): boolean {
    let checkForPre = '';
    if (assessment?.reports) checkForPre = (assessment?.reports[(assessment?.reports?.length ?? 1) - 1]?.report_type ?? '');
    return (assessment.assessment_status_id == 1 || assessment.assessment_status_id == 3 || assessment.assessment_status_id == 4 || assessment.assessment_status_id == 12)
      && (assessment?.reports?.length == 0 || (checkForPre == 'Pre-assessment Request'))
      && (this.user.isTeamLead || this.user.isROCO || this.user.isFOCO)
  }

  checkCreateInterimReport(assessment: any): boolean {
    return ((assessment.assessment_status_id >= 3) && assessment.reports?.length >= 1)
      && ((assessment.reports[assessment.reports?.length - 1].initial_report_type ?? '') != '')
      && ((assessment.reports[assessment.reports?.length - 1].report_type ?? '') != 'Pre-assessment Request')
      && (this.user.isTeamLead || this.user.isROCO || this.user.isFOCO)
  }

  createBarcImageRequest(assessment: any) {
    this.dialog.open(BarcRequestComponent, {
      maxWidth: "1000px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
      data: {
        fireRecord: assessment,
        isTeamLead: this.user.isTeamLead,
        user: this.user
      },
    }).afterClosed().subscribe((result) => {
      this.titleService.setTitle("BAER - Assessments")
      if (result === 'success') {
        this.toast.openSuccessSnackBar(`A BARC Image request ${assessment.baer_name} has been submitted.`);
        this.loadAssessments();
      } else if (result === 'error') {
        this.toast.openErrorSnackBar(`A BARC Image request ${assessment.baer_name} failed to submit.`);
      }
    })
  }
}
